import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import {
  addNovaLimaMatricula,
  checksumMatriculas,
  loadNovaLimaAdminSnapshot,
  normalizeMatricula,
  parseMatriculaColumn,
  removeNovaLimaMatricula,
  replaceNovaLimaBase,
  restoreNovaLimaPreviousBase,
  type MatriculaParseResult,
  type NovaLimaAdminSnapshot,
} from '../../lib/novaLimaMatriculas'

const EMPTY_PARSE: MatriculaParseResult = { matriculas: [], sourceRows: 0, duplicates: 0, invalid: [] }

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString('pt-BR') : '—'
}

export function NovaLimaMatriculasAdmin({ onBack }: { onBack: () => void }) {
  const [snapshot, setSnapshot] = useState<NovaLimaAdminSnapshot>({ active: null, previous: null, matriculas: [] })
  const [loading, setLoading] = useState(true)
  const [file, setFile] = useState<File | null>(null)
  const [parsed, setParsed] = useState<MatriculaParseResult>(EMPTY_PARSE)
  const [checksum, setChecksum] = useState('')
  const [processingFile, setProcessingFile] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [manualMatricula, setManualMatricula] = useState('')
  const [search, setSearch] = useState('')
  const [savingManual, setSavingManual] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function load() {
    setLoading(true)
    setError('')
    try {
      setSnapshot(await loadNovaLimaAdminSnapshot())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar a gestão de matrículas.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  const filtered = useMemo(() => {
    const term = normalizeMatricula(search)
    if (!term) return snapshot.matriculas
    return snapshot.matriculas.filter((item) => item.includes(term))
  }, [snapshot.matriculas, search])

  async function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    const selected = event.target.files?.[0] ?? null
    setFile(selected)
    setParsed(EMPTY_PARSE)
    setChecksum('')
    setMessage('')
    setError('')
    if (!selected) return

    setProcessingFile(true)
    try {
      const buffer = await selected.arrayBuffer()
      const workbook = XLSX.read(buffer, { type: 'array' })
      const sheetName = workbook.SheetNames[0]
      if (!sheetName) throw new Error('O arquivo não possui nenhuma planilha.')
      const sheet = workbook.Sheets[sheetName]
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' }) as unknown[][]
      const result = parseMatriculaColumn(rows)
      if (result.matriculas.length === 0) throw new Error('Nenhuma matrícula válida foi encontrada na primeira coluna.')
      const digest = await checksumMatriculas(result.matriculas)
      setParsed(result)
      setChecksum(digest)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível ler o arquivo.')
      setFile(null)
    } finally {
      setProcessingFile(false)
      event.target.value = ''
    }
  }

  async function publish() {
    if (!file || !checksum || parsed.matriculas.length === 0 || parsed.invalid.length > 0) return
    const before = snapshot.active?.row_count ?? 0
    if (!window.confirm(`Substituir a base vigente?\n\n${before} matrículas → ${parsed.matriculas.length} matrículas\n\nA troca será atômica e a base atual ficará disponível como versão anterior para rollback.`)) return

    setPublishing(true)
    setError('')
    setMessage('')
    try {
      await replaceNovaLimaBase(file.name, checksum, parsed.matriculas)
      setMessage(`Nova base publicada com sucesso: ${parsed.matriculas.length} matrículas vigentes.`)
      setFile(null)
      setParsed(EMPTY_PARSE)
      setChecksum('')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível substituir a base.')
    } finally {
      setPublishing(false)
    }
  }

  async function addManual(event: FormEvent) {
    event.preventDefault()
    const normalized = normalizeMatricula(manualMatricula)
    if (!normalized) return setError('Informe uma matrícula válida usando somente números.')
    setSavingManual(true)
    setError('')
    setMessage('')
    try {
      await addNovaLimaMatricula(normalized)
      setManualMatricula('')
      setMessage(`Matrícula ${normalized} adicionada à base vigente.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível adicionar a matrícula.')
    } finally {
      setSavingManual(false)
    }
  }

  async function removeManual(matricula: string) {
    if (!window.confirm(`Excluir a matrícula ${matricula} da base vigente?`)) return
    setSavingManual(true)
    setError('')
    setMessage('')
    try {
      await removeNovaLimaMatricula(matricula)
      setMessage(`Matrícula ${matricula} removida da base vigente.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível excluir a matrícula.')
    } finally {
      setSavingManual(false)
    }
  }

  async function restorePrevious() {
    if (!snapshot.previous) return
    if (!window.confirm(`Restaurar a base anterior "${snapshot.previous.source_filename}" com ${snapshot.previous.row_count} matrículas? A base atual ficará como versão anterior.`)) return
    setPublishing(true)
    setError('')
    setMessage('')
    try {
      await restoreNovaLimaPreviousBase()
      setMessage('Base anterior restaurada com sucesso.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível restaurar a base anterior.')
    } finally {
      setPublishing(false)
    }
  }

  function exportCurrent() {
    if (!snapshot.active || snapshot.matriculas.length === 0) return
    const sheet = XLSX.utils.aoa_to_sheet([['Matrícula'], ...snapshot.matriculas.map((item) => [item])])
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, sheet, 'Matrículas')
    XLSX.writeFile(workbook, `Nova_Lima_Regiao_Matriculas_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  return (
    <section className="workspace-content admin-editor nova-lima-admin">
      <div className="page-heading">
        <div>
          <button className="text-button" onClick={onBack}>← Voltar ao painel</button>
          <p className="eyebrow">REGRAS OPERACIONAIS</p>
          <h1>Nova Lima e Região - Matrículas</h1>
          <p className="muted">Substitua a base vigente por Excel ou faça ajustes pontuais. As alterações passam a valer imediatamente para a operação.</p>
        </div>
        <span className="status-chip">Gestão da base</span>
      </div>

      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}
      {loading && <div className="empty-state">Carregando base...</div>}

      {!loading && (
        <>
          <div className="nova-lima-admin-metrics">
            <article><span>Base vigente</span><strong>{snapshot.active?.row_count ?? 0}</strong><small>matrículas</small></article>
            <article><span>Última atualização</span><strong>{snapshot.active ? formatDate(snapshot.active.imported_at) : '—'}</strong><small>{snapshot.active?.source_filename ?? 'Nenhum arquivo publicado'}</small></article>
            <article><span>Rollback</span><strong>{snapshot.previous ? snapshot.previous.row_count : '—'}</strong><small>{snapshot.previous?.source_filename ?? 'Sem base anterior'}</small></article>
          </div>

          <section className="editor-form-card nova-lima-import-card">
            <div className="admin-section-title">
              <div><p className="eyebrow">SUBSTITUIR BASE</p><h2>Importar Excel</h2><p className="muted">Use um arquivo .xlsx ou .xls com uma única coluna de matrículas. O cabeçalho “Matrícula” é opcional.</p></div>
              {snapshot.previous && <button type="button" className="secondary-button compact" onClick={() => void restorePrevious()} disabled={publishing}>Restaurar base anterior</button>}
            </div>

            <label className="nova-lima-file-field">
              Arquivo Excel (.xlsx / .xls)
              <input type="file" accept=".xlsx,.xls" onChange={onFileChange} disabled={processingFile || publishing} />
            </label>

            {processingFile && <div className="notice info">Lendo e validando o arquivo...</div>}
            {file && (
              <div className="nova-lima-import-preview">
                <div><span>Arquivo</span><strong>{file.name}</strong></div>
                <div><span>Linhas recebidas</span><strong>{parsed.sourceRows}</strong></div>
                <div><span>Matrículas únicas</span><strong>{parsed.matriculas.length}</strong></div>
                <div><span>Duplicadas removidas</span><strong>{parsed.duplicates}</strong></div>
                <div><span>Inválidas</span><strong className={parsed.invalid.length ? 'danger-text' : ''}>{parsed.invalid.length}</strong></div>
              </div>
            )}

            {parsed.invalid.length > 0 && (
              <div className="notice error">Existem {parsed.invalid.length} valor(es) inválido(s) na primeira coluna. Corrija o Excel antes de publicar. Exemplos: {parsed.invalid.slice(0, 5).join(', ')}.</div>
            )}

            {file && parsed.matriculas.length > 0 && parsed.invalid.length === 0 && (
              <div className="nova-lima-import-actions">
                <div className="notice info">A base atual só será trocada depois da validação completa. A substituição é atômica.</div>
                <button type="button" className="primary-button compact" onClick={() => void publish()} disabled={publishing}>
                  {publishing ? 'Publicando...' : snapshot.active ? 'Substituir base vigente' : 'Publicar primeira base'}
                </button>
              </div>
            )}
          </section>

          <section className="editor-form-card">
            <div className="admin-section-title">
              <div><p className="eyebrow">AJUSTES PONTUAIS</p><h2>Adicionar ou excluir matrícula</h2><p className="muted">Use esta área somente para correções entre uma atualização de Excel e outra.</p></div>
              <button type="button" className="secondary-button compact" onClick={exportCurrent} disabled={!snapshot.active}>Exportar base atual</button>
            </div>

            <form className="nova-lima-manual-form" onSubmit={addManual}>
              <label>Nova matrícula<input inputMode="numeric" value={manualMatricula} onChange={(event) => setManualMatricula(event.target.value.replace(/\D/g, ''))} placeholder="Ex.: 17863609" /></label>
              <button type="submit" className="primary-button compact" disabled={savingManual || !snapshot.active}>Adicionar</button>
            </form>

            <div className="nova-lima-list-toolbar">
              <input type="search" inputMode="numeric" value={search} onChange={(event) => setSearch(event.target.value.replace(/\D/g, ''))} placeholder="Buscar matrícula na base vigente..." />
              <span>{filtered.length.toLocaleString('pt-BR')} resultado(s)</span>
            </div>

            <div className="nova-lima-admin-list">
              {filtered.slice(0, 250).map((item) => (
                <div key={item}><strong>{item}</strong><button type="button" className="danger-link" onClick={() => void removeManual(item)} disabled={savingManual}>Excluir</button></div>
              ))}
              {filtered.length > 250 && <div className="nova-lima-list-limit">Exibindo os primeiros 250 resultados. Refine a busca para localizar uma matrícula específica.</div>}
              {filtered.length === 0 && <div className="empty-state">Nenhuma matrícula encontrada.</div>}
            </div>
          </section>
        </>
      )}
    </section>
  )
}
